import moment from 'moment'
import settings from 'popups/Settings'

import julian from 'julian'

/**
 * Limit date range for calendar operations
 */
export const LIMIT_DATE = {
  startDate: new Date(-8640000000000000),
  endDate: new Date(8640000000000000),
}

/**
 * Get the first day of the month
 */
export function getMonthFirstDate(date: Date): Date {
  const tempDate = new Date(date)
  tempDate.setDate(1)
  return tempDate
}

/**
 * Get the last day of the month
 */
export function getMonthLastDate(date: Date): Date {
  let tempDate = new Date(date)
  tempDate.setMonth(date.getMonth() + 1, 1)
  tempDate = dateAddingDate(tempDate, -1)
  return tempDate
}

/**
 * Get the first day of the week based on settings
 */
export function getWeekFirstDate(date: Date): Date {
  let firstWeekday = settings.getValue('calendar').firstdayOfWeek
  if (firstWeekday === 7) {
    firstWeekday = moment.localeData().firstDayOfWeek()
  }
  const weekday = date.getDay()

  if (firstWeekday === 1) {
    // monday first
    if (weekday === 0) {
      date = dateAddingDate(date, -6)
    } else if (weekday !== 1) {
      date = dateAddingDate(date, -(weekday - 1))
    }
  } else if (firstWeekday === 6) {
    // saturday fisrt
    if (weekday !== 6) {
      date = dateAddingDate(date, -(weekday + 1))
    }
  } else if (firstWeekday === 0) {
    // sunday first
    if (weekday !== 0) {
      date = dateAddingDate(date, -weekday)
    }
  } else {
    throw new Error('Invalid firstWeekday')
  }
  return date
}

/**
 * Get the last day of the week based on settings
 */
export function getWeekLastDate(date: Date): Date {
  let firstWeekday = settings.getValue('calendar').firstdayOfWeek
  if (firstWeekday === 7) {
    firstWeekday = moment.localeData().firstDayOfWeek()
  }
  const weekday = date.getDay()

  if (firstWeekday === 1) {
    // monday first - last day is sunday
    if (weekday !== 0) {
      date = dateAddingDate(date, 7 - weekday)
    }
  } else if (firstWeekday === 6) {
    // saturday first - last day is friday
    if (weekday !== 5) {
      if (weekday === 6) {
        date = dateAddingDate(date, 6)
      } else {
        date = dateAddingDate(date, 5 - weekday)
      }
    }
  } else if (firstWeekday === 0) {
    // sunday first - last day is saturday
    if (weekday !== 6) {
      date = dateAddingDate(date, 6 - weekday)
    }
  } else {
    throw new Error('Invalid firstWeekday')
  }
  return date
}

/**
 * Get the first day of the previous month
 */
export function getPrevMonth(date: Date): Date {
  const tempDate = new Date(date)
  tempDate.setMonth(date.getMonth() - 1, 1)
  return tempDate
}

/**
 * Get the first day of the next month
 */
export function getNextMonth(date: Date): Date {
  const tempDate = new Date(date)
  tempDate.setMonth(date.getMonth() + 1, 1)
  return tempDate
}

/**
 * Reset time to midnight (00:00:00.000)
 */
export function getDateZero(date: Date): Date {
  const tempDate = new Date(date)
  tempDate.setHours(0)
  tempDate.setMinutes(0)
  tempDate.setSeconds(0)
  tempDate.setMilliseconds(0)
  return tempDate
}

/**
 * Get the first date to display in month view (includes previous month days)
 */
export function getMonthViewFirstDate(aDate: Date): Date {
  const date = new Date(aDate)
  date.setHours(0)
  date.setMinutes(0)
  date.setSeconds(0)
  date.setMilliseconds(0)
  date.setDate(1)

  return getWeekFirstDate(date)
}

/**
 * Get the last date to display in month view (includes next month days)
 */
export function getMonthViewLastDate(aDate: Date): Date {
  let date = getMonthLastDate(aDate)
  date.setHours(0)
  date.setMinutes(0)
  date.setSeconds(0)
  date.setMilliseconds(0)

  date = getWeekLastDate(date)
  return date
}

/**
 * Add or subtract days from a date
 */
export function dateAddingDate(date: Date, dateOffset: number = 1): Date {
  const tempDate = new Date(date)
  if (dateOffset === 0) {
    return tempDate
  }

  tempDate.setDate(date.getDate() + dateOffset)
  return tempDate
}

/**
 * Convert date to UTC datetime string format (YYYYMMDDTHHmmssZ)
 */
export function utcDateTimeString(date: Date): string {
  const y = date.getUTCFullYear()
  const m = date.getUTCMonth() + 1
  const d = date.getUTCDate()
  const h = date.getUTCHours()
  const min = date.getUTCMinutes()
  const s = date.getUTCSeconds()
  return (
    y.toString().padStart(4, '0') +
    m.toString().padStart(2, '0') +
    d.toString().padStart(2, '0') +
    'T' +
    h.toString().padStart(2, '0') +
    min.toString().padStart(2, '0') +
    s.toString().padStart(2, '0') +
    'Z'
  )
}

/**
 * Calculate the number of days between two dates
 */
export function betweenDay(date1: Date, date2: Date): number {
  return julian.toJulianDay(getDateZero(date2)) - julian.toJulianDay(getDateZero(date1))
}

/**
 * Check if two dates are on the same day
 */
export function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  )
}

let isYearFirst: boolean | null = null
/**
 * Check if the localized date format starts with year
 */
export function isLocalizedYearFirst(): boolean {
  if (isYearFirst === null) {
    isYearFirst = moment().localeData().longDateFormat('LL').indexOf('YYYY') === 0
  }
  return isYearFirst
}
isLocalizedYearFirst()

/**
 * Format date as localized string with weekday (e.g., "Mon, Jan 1, 2024")
 */
export function localizedDateString_ddddMMMMDYYYY(date: Date | string): string {
  const locale = settings.getLocale()

  // date가 Date 객체가 아닌 경우 변환
  const dateObj = date instanceof Date ? date : new Date(date)

  // 유효한 날짜인지 확인
  if (isNaN(dateObj.getTime())) {
    console.warn('Invalid date passed to localizedDateString_ddddMMMMDYYYY:', date)
    return date.toString() // 원본 값 반환
  }

  return dateObj.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    weekday: 'short',
  })
}

/**
 * Format date as localized string based on settings
 */
export function localizedDateString(date: Date): string {
  const locale = settings.getLocale()
  const dateFormat = settings.getValue('general').dateFormat
  const mDate = moment(date).locale(locale).format(dateFormat)
  return mDate.toString()
}

/**
 * Get localized short day name (e.g., "Mon")
 */
export function localizedDayString(date: Date): string {
  const locale = settings.getLocale()
  const mDate = moment(date).locale(locale).format('ddd')
  return mDate.toString()
}

/**
 * Format time as localized string based on settings
 */
export function localizedTimeString(date: Date): string {
  const locale = settings.getLocale()
  const timeFormat = settings.getValue('general').timeFormat
  if (timeFormat === 'h:mm A') {
    return date.toLocaleTimeString(locale, {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    })
  } else {
    return date.toLocaleTimeString(locale, {
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    })
  }
}

/**
 * Format time with optional minute removal (for Month Event Cell display)
 * Removes ":00" from hour-only times in 12-hour format
 */
export function localizedTimeStringRemoveMinute(date: Date): string {
  const locale = settings.getLocale()
  const timeFormat = settings.getValue('general').timeFormat
  if (timeFormat === 'h:mm A') {
    const minute = date.getMinutes()
    const timeString = date.toLocaleTimeString(locale, {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    })
    if (minute === 0) {
      return timeString.replace(':00', '')
    }
    return timeString
  } else {
    return date.toLocaleTimeString(locale, {
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    })
  }
}

/**
 * Convert date to UTC date string format using moment
 */
export function utcDateString(date: Date): string {
  return moment(date).utc().format('YYYYMMDDTHHmmss') + 'Z'
}

/**
 * Get week number for a date based on calendar settings
 * @returns Week number, or 0 if week numbers are disabled
 */
export function getWeekNumber(date: Date): number {
  const calendarSettings = settings.getValue('calendar')
  // const showWeekNumber = calendarSettings.showWeekNumber
  // if (!showWeekNumber) {
  //   return 0
  // }

  const firstWeekday = calendarSettings.firstdayOfWeek
  const firstWeekNumber = calendarSettings.firstWeekNumber

  const momentLocaleData = moment.localeData()

  let dow: number = firstWeekday
  if (firstWeekday === 7) {
    dow = momentLocaleData.firstDayOfWeek()
  } else {
    dow = firstWeekday
  }

  let doy: number = 0
  if (firstWeekday !== 7 || firstWeekNumber !== 0) {
    if (firstWeekNumber === 0) {
      doy = dow + 6
    } else if (firstWeekNumber === 1) {
      doy = dow + 3
    } else if (firstWeekNumber === 2) {
      doy = dow
    }

    const oDow = momentLocaleData.firstDayOfWeek()
    const oDoy = momentLocaleData.firstDayOfYear()

    moment.updateLocale(moment.locale(), {
      week: {
        dow: dow,
        doy: doy,
      },
    })

    // const ld = moment().localeData()
    // console.log('ld %o', ld)

    const week = moment(date).week()
    // console.log('week %d', week)

    moment.updateLocale(moment.locale(), {
      week: {
        dow: oDow,
        doy: oDoy,
      },
    })

    return week
  }
  return moment(date).week()
}
